import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useSchedule } from '../api/queries'
import type { Booking, ScheduleRoom } from '../api/types'
import { BookingDialog } from '../components/BookingDialog'
import { StateBadge } from '../components/StateBadge'
import { Button, Empty, Loading, PageTitle } from '../components/ui'
import { ErrorNotice } from '../components/ErrorNotice'
import { RoomImage } from '../components/RoomImage'
import { cn } from '../lib/cn'
import { seats } from '../lib/words'
import {
  addDaysToKey,
  atWallTime,
  clockOffset,
  fmtDayTitle,
  fmtRange,
  fmtTime,
  fromDateKey,
  isToday,
  minutesBetween,
  pad,
  serverNow,
  todayKey,
  toIso,
  SLOT_MINUTES,
} from '../lib/time'
import { useCanBook } from '../session'

/**
 * Экран 2.1 «День» — таймлайн: комнаты строками, время колонками.
 *
 * Одним запросом на весь день, а не по комнате: смысл этого экрана в том, чтобы вся
 * картина была видна сразу. Красная черта — текущее время, и считается она от часов
 * сервера: разошедшиеся часы устройства нарисовали бы её не там, причём тихо.
 *
 * На узком экране таймлайн превращается в список комнат с состоянием (экран 3.1):
 * горизонтальная сетка на 360 точках нечитаема, а список — нет.
 */
export function DayPage() {
  const [params, setParams] = useSearchParams()
  const date = params.get('date') ?? todayKey()
  const canBook = useCanBook()
  const navigate = useNavigate()

  const { data, isLoading, error, refetch } = useSchedule(date)
  const [openBooking, setOpenBooking] = useState<Booking | null>(null)

  const offset = clockOffset(data?.server_time)
  const now = useTicking(offset)

  function setDate(next: string) {
    setParams(next === todayKey() ? {} : { date: next }, { replace: true })
  }

  return (
    <>
      <PageTitle
        action={
          canBook && (
            <Button variant="primary" onClick={() => navigate(`/search?date=${date}`)}>
              Забронировать
            </Button>
          )
        }
      >
        {fmtDayTitle(fromDateKey(date))}
      </PageTitle>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {/* У кнопок только стрелка, поэтому подпись для чтения с экрана обязательна:
            «ссылка ←» не говорит ничего. */}
        <Button
          size="sm"
          variant="ghost"
          aria-label="Предыдущий день"
          title="Предыдущий день"
          onClick={() => setDate(addDaysToKey(date, -1))}
        >
          <span aria-hidden>←</span>
        </Button>
        <Button
          size="sm"
          variant={isToday(date) ? 'quiet' : 'ghost'}
          onClick={() => setDate(todayKey())}
        >
          Сегодня
        </Button>
        <Button
          size="sm"
          variant="ghost"
          aria-label="Следующий день"
          title="Следующий день"
          onClick={() => setDate(addDaysToKey(date, 1))}
        >
          <span aria-hidden>→</span>
        </Button>
        <input
          type="date"
          aria-label="Дата расписания"
          className="field tnum ml-1 h-8 w-auto py-0 text-sm"
          value={date}
          onChange={(event) => event.target.value && setDate(event.target.value)}
        />
        {isToday(date) && (
          <span className="tnum ml-auto text-sm text-muted">
            Сейчас {fmtTime(now)} — время сервера
          </span>
        )}
      </div>

      {error && <ErrorNotice error={error} onRetry={() => void refetch()} className="mb-4" />}
      {isLoading && <Loading text="Собираем расписание дня…" />}

      {data && data.rooms.length === 0 && (
        <Empty
          title="Переговорных пока нет"
          hint="Комнаты заводит администратор в разделе «Управление» — там же задаются вместимость и оснащение."
        />
      )}

      {data && data.rooms.length > 0 && (
        <>
          {/* Широкий экран: таймлайн */}
          <div className="hidden md:block">
            <Timeline
              date={date}
              rooms={data.rooms}
              /* Границы сетки берём из ответа расписания, а не из общих правил:
                 сервер уже свёл окна всех комнат, и у комнаты с собственными
                 часами колонки иначе обрезали бы её рабочее время. */
              workStart={data.work_start}
              workEnd={data.work_end}
              now={now}
              showNowLine={isToday(date)}
              onOpenBooking={setOpenBooking}
              onPickFreeSlot={
                canBook
                  ? (roomId, startsAt, endsAt) =>
                      navigate(
                        `/book?room=${roomId}&start=${encodeURIComponent(startsAt)}&end=${encodeURIComponent(endsAt)}`,
                      )
                  : undefined
              }
            />
          </div>

          {/* Узкий экран: список комнат с состоянием (экран 3.1) */}
          <div className="flex flex-col gap-3 md:hidden">
            {data.rooms.map((row) => (
              <RoomCard key={row.room.id} row={row} date={date} now={now} />
            ))}
          </div>
        </>
      )}

      {openBooking && (
        <BookingDialog bookingId={openBooking.id} onClose={() => setOpenBooking(null)} />
      )}
    </>
  )
}

/**
 * Самая узкая сетка таймлайна в точках.
 *
 * Число заведено константой, а не классом min-w-[720px], потому что от него зависит
 * не только вёрстка: по нему считается, помещается ли в блок брони время целиком.
 * Разъехавшись, эти два места дали бы обрезанное «14:00 – …» ровно там, где расчёт
 * обещает, что всё поместилось.
 */
const GRID_MIN_PX = 720

/** Уже этой ширины блок не вмещает диапазон «14:00 – 15:00» вместе с полями. */
const RANGE_FITS_PX = 96

function Timeline({
  date,
  rooms,
  workStart,
  workEnd,
  now,
  showNowLine,
  onOpenBooking,
  onPickFreeSlot,
}: {
  date: string
  rooms: ScheduleRoom[]
  workStart: string
  workEnd: string
  now: Date
  showNowLine: boolean
  onOpenBooking: (booking: Booking) => void
  onPickFreeSlot?: (roomId: number, startsAt: string, endsAt: string) => void
}) {
  const dayStart = atWallTime(date, workStart)
  const dayEnd = atWallTime(date, workEnd)
  const totalMinutes = Math.max(60, Math.round((dayEnd.getTime() - dayStart.getTime()) / 60_000))

  const hours = useMemo(() => {
    const marks: Date[] = []
    const cursor = new Date(dayStart)
    cursor.setMinutes(0, 0, 0)
    if (cursor < dayStart) cursor.setHours(cursor.getHours() + 1)
    while (cursor <= dayEnd) {
      marks.push(new Date(cursor))
      cursor.setHours(cursor.getHours() + 1)
    }
    return marks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, workStart, workEnd])

  const percent = (moment: Date) =>
    ((moment.getTime() - dayStart.getTime()) / (totalMinutes * 60_000)) * 100

  const nowPercent = percent(now)
  const nowVisible = showNowLine && nowPercent >= 0 && nowPercent <= 100

  return (
    <div className="card overflow-hidden">
      <div className="flex">
        {/* Колонка комнат не уезжает при прокрутке: без названия полоса ничего не значит. */}
        <div className="w-52 shrink-0 border-r border-line bg-surface-2">
          <div className="h-9 border-b border-line" />
          {rooms.map((row) => (
            <div key={row.room.id} className="flex h-20 flex-col justify-center gap-1 border-b border-line px-3 last:border-b-0">
              <Link
                to={`/rooms/${row.room.id}`}
                className="truncate text-sm font-medium hover:text-accent"
                title={`${row.room.name} — открыть слоты на день`}
              >
                {row.room.name}
              </Link>
              <div className="flex items-center gap-2">
                <StateBadge state={row.state} freeUntil={row.free_until} now={now} />
                <span className="text-xs text-muted">{seats(row.room.capacity)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="relative min-w-0 flex-1 overflow-x-auto">
          <div style={{ minWidth: `${GRID_MIN_PX}px` }}>
            {/* Шкала часов */}
            <div className="relative h-9 border-b border-line">
              {hours.map((hour) => {
                const left = percent(hour)
                const atRightEdge = left >= 100
                return (
                  <span
                    key={hour.toISOString()}
                    className={cn(
                      'tnum absolute top-2 text-xs text-muted',
                      // Крайние подписи не центруются по метке, иначе половина «09:00»
                      // уезжает под колонку комнат, а «20:00» — за правый край.
                      left < 1 || atRightEdge
                        ? 'translate-x-0'
                        : left > 99
                          ? '-translate-x-full'
                          : '-translate-x-1/2',
                    )}
                    // Последняя подпись прижимается к правому краю, а не сдвигается
                    // от left: 100% — см. соседний комментарий про лишний пиксель
                    // и вечную полосу прокрутки.
                    style={atRightEdge ? { right: 0 } : { left: `${left}%` }}
                  >
                    {fmtTime(hour)}
                  </span>
                )
              })}
            </div>

            {rooms.map((row) => (
              <div
                key={row.room.id}
                className="relative h-20 border-b border-line last:border-b-0"
                onClick={(event) => {
                  if (!onPickFreeSlot) return
                  const rect = event.currentTarget.getBoundingClientRect()
                  const ratio = (event.clientX - rect.left) / rect.width
                  const slot = slotAt(dayStart, totalMinutes, ratio, now, dayEnd)
                  if (slot) onPickFreeSlot(row.room.id, slot.startsAt, slot.endsAt)
                }}
                /* role="button" здесь был неправдой: с клавиатуры на полосу не попасть
                   и нажать её нечем. Мышью — можно, а клавиатурный путь к тому же
                   результату идёт через название комнаты в левой колонке. */
                title={onPickFreeSlot ? 'Нажмите на свободное место, чтобы забронировать' : undefined}
              >
                {/* Получасовая сетка — она же подсказка о шаге бронирования.
                    Линия последнего часа ставится от правого края, а не в left: 100%:
                    её ширина в один пиксель выходила за сетку, и горизонтальная полоса
                    прокрутки была нарисована всегда — даже когда день с 09:00 до 20:00
                    помещается целиком. Прокручивался ровно этот один пиксель. */}
                <div className="pointer-events-none absolute inset-0">
                  {hours.map((hour) => {
                    const left = percent(hour)
                    return (
                      <span
                        key={hour.toISOString()}
                        className="absolute top-0 h-full w-px bg-line"
                        style={left >= 100 ? { right: 0 } : { left: `${left}%` }}
                      />
                    )
                  })}
                </div>

                {/*
                  Нерабочее время этой комнаты. Сетка одна на всех, а часы
                  у комнат разные: без штриховки строка комнаты, открытой
                  с 10:00, выглядит свободной с 08:00 — человек нажимает,
                  и сервер отказывает. Поля некликабельны, но клик по ним
                  проходит на полосу ниже: slotAt всё равно вернёт время,
                  которое сервер отклонит с понятным объяснением — оно
                  честнее молчаливого отсутствия реакции.
                */}
                <div className="pointer-events-none absolute inset-0">
                  {(() => {
                    const openAt = atWallTime(date, row.work_start)
                    const closeAt = atWallTime(date, row.work_end)
                    const before = Math.max(0, Math.min(100, percent(openAt)))
                    const after = Math.max(0, Math.min(100, percent(closeAt)))
                    return (
                      <>
                        {before > 0 && (
                          <span
                            className="absolute inset-y-0 left-0 bg-surface-3/70"
                            style={{ width: `${before}%` }}
                            title={`Комната работает с ${row.work_start}`}
                          />
                        )}
                        {after < 100 && (
                          <span
                            className="absolute inset-y-0 right-0 bg-surface-3/70"
                            style={{ width: `${100 - after}%` }}
                            title={`Комната работает до ${row.work_end}`}
                          />
                        )}
                      </>
                    )
                  })()}
                </div>

                {row.bookings.map((booking) => {
                  const left = Math.max(0, percent(new Date(booking.starts_at)))
                  const right = Math.min(100, percent(new Date(booking.ends_at)))
                  if (right <= 0 || left >= 100) return null
                  const widthPercent = Math.max(1.2, right - left)
                  // Оценка ширины блока снизу: сетка не бывает уже GRID_MIN_PX.
                  // Часовая бронь в дне с 09:00 до 20:00 — это 65 точек, и «14:00 – 15:00»
                  // в них превращалось в «14:00 – …». Обрезанное время читается как
                  // поломка; лучше показать одно начало, оно помещается целиком.
                  const roomy = (widthPercent / 100) * GRID_MIN_PX >= RANGE_FITS_PX
                  return (
                    <button
                      key={booking.id}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpenBooking(booking)
                      }}
                      style={{ left: `${left}%`, width: `${widthPercent}%` }}
                      className={cn(
                        'absolute top-2 flex h-16 flex-col justify-center overflow-hidden rounded-lg border px-2 text-left transition',
                        booking.source === 'kiosk'
                          ? 'border-dashed border-line bg-surface-3 hover:border-fg/40'
                          : 'border-accent/30 bg-accent-soft hover:border-accent',
                      )}
                      title={`${booking.title || 'Занято'} · ${fmtRange(booking.starts_at, booking.ends_at)}`}
                    >
                      {/* В узком блоке тема занимает обе строки: одно слово с многоточием
                          не говорит ни о чём, а две строки чаще всего вмещают тему целиком.
                          Полный текст в любом случае есть в подсказке и в карточке встречи. */}
                      <span className={cn('text-sm font-medium', roomy ? 'truncate' : 'line-clamp-2')}>
                        {booking.title || 'Занято'}
                      </span>
                      <span className="tnum truncate text-xs text-muted">
                        {roomy ? fmtRange(booking.starts_at, booking.ends_at) : fmtTime(booking.starts_at)}
                      </span>
                    </button>
                  )
                })}
              </div>
            ))}

            {/* Линия текущего времени. Считается от часов сервера, не устройства. */}
            {nowVisible && (
              <div
                className="pointer-events-none absolute bottom-0 top-9 w-px bg-state-busy"
                style={{ left: `${nowPercent}%` }}
              >
                <span className="tnum absolute -top-[26px] -translate-x-1/2 rounded bg-state-busy px-1 py-0.5 text-[11px] font-medium text-on-state">
                  {fmtTime(now)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function RoomCard({ row, date, now }: { row: ScheduleRoom; date: string; now: Date }) {
  const next = row.bookings.find((booking) => new Date(booking.ends_at) > now)
  // Экран 3.2: час выбран заранее. С мобильного списка комнат человек приходит
  // за «сейчас», а не за абстрактным днём, — на экране слотов текущий получас
  // уже отмечен, и остаётся только подтвердить.
  const at = isToday(date) ? `&at=${pad(now.getHours())}:${now.getMinutes() < 30 ? '00' : '30'}` : ''
  return (
    <Link
      to={`/rooms/${row.room.id}?date=${date}${at}`}
      className="card flex items-center gap-3 px-3 py-3 transition hover:border-accent"
    >
      {/*
        Картинка комнаты делает список узнаваемым: на телефоне названия
        «Переговорная 3» и «Переговорная 4» соседними строками почти не
        различаются, а рисунки различаются сразу.

        96×56 — почти те же 16:9, что и у самого рисунка, поэтому по краям
        срезается пара пикселей, а не половина комнаты.
      */}
      <RoomImage
        name={row.room.name}
        capacity={row.room.capacity}
        photoUrl={row.room.photo_url}
        className="h-14 w-24 shrink-0 rounded-md"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{row.room.name}</p>
        {/*
          Вместимость и ближайшая бронь — двумя строками, а не одной.
          Одной строкой на телефоне выходило «20 мест · дальше ...»: фраза обрывалась
          на середине, и от неё оставалась ровно та часть, которую человек и так знает.
          Теперь обрезается только тема встречи — она в конце и договаривать её
          не обязательно, а время видно целиком.
        */}
        <p className="text-sm text-muted">{seats(row.room.capacity)}</p>
        {next && (
          <p className="flex gap-1 text-sm text-muted">
            <span className="tnum shrink-0">Дальше {fmtRange(next.starts_at, next.ends_at)}</span>
            <span className="truncate">— {next.title || 'занято'}</span>
          </p>
        )}
      </div>
      <StateBadge state={row.state} freeUntil={row.free_until} now={now} />
    </Link>
  )
}

/**
 * Слот под курсором. Начало кратно тридцати минутам, но если попали в текущий получас,
 * бронь начинается с текущей минуты: в 13:45 можно занять «сейчас — 14:30», иначе
 * пятнадцать минут пропадают впустую.
 */
function slotAt(
  dayStart: Date,
  totalMinutes: number,
  ratio: number,
  now: Date,
  dayEnd: Date,
): { startsAt: string; endsAt: string } | null {
  const clamped = Math.min(Math.max(ratio, 0), 1)
  const minutesFromStart = Math.floor((clamped * totalMinutes) / SLOT_MINUTES) * SLOT_MINUTES
  let start = new Date(dayStart.getTime() + minutesFromStart * 60_000)
  const slotEnd = new Date(start.getTime() + SLOT_MINUTES * 60_000)

  if (slotEnd <= now) return null // прошедшее время не бронируется
  if (start < now) start = new Date(now.getTime())

  const end = new Date(Math.min(slotEnd.getTime() + SLOT_MINUTES * 60_000, dayEnd.getTime()))
  if (minutesBetween(toIso(start), toIso(end)) < SLOT_MINUTES) {
    return { startsAt: toIso(start), endsAt: toIso(slotEnd) }
  }
  return { startsAt: toIso(start), endsAt: toIso(end) }
}

/** Тик раз в полминуты: линия времени должна ползти, но не жечь батарею. */
function useTicking(offsetMs: number): Date {
  const [, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 30_000)
    return () => clearInterval(timer)
  }, [])
  return serverNow(offsetMs)
}
